// App.js - VERSÃO CORRIGIDA: Navegação entre telas + StreamViewScreen + ValidationScreen
import React, { useState, useEffect } from 'react';
import {
  ScrollView,
  StyleSheet,
  View,
  StatusBar,
  Text,
  Platform,
  SafeAreaView,
  ActivityIndicator,
} from 'react-native';
import { Provider as PaperProvider } from 'react-native-paper';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { paperTheme } from './src/theme/theme';
import HeaderComponent from './src/components/HeaderComponent';
import AnnouncementBanner from './src/components/AnnouncementBanner';
import FooterComponent from './src/components/FooterComponent';
import FormListScreen from './src/screens/FormListScreen';
import FormDetailScreen from './src/screens/FormDetailScreen';
import ResponsesScreen from './src/screens/ResponsesScreen';
import FormCreatorScreen from './src/screens/FormCreatorScreen';
import StreamViewScreen from './src/screens/StreamViewScreen';
import LoginModal from './src/components/LoginModal';
import SyncService from './src/lib/SyncService';
import AuthService from './src/services/authService';
import ResultsScreen from './src/screens/ResultsScreen';
import ValidationScreen from './src/screens/ValidationScreen';

// ✅ CLASSE INDEXNOW SEM PROBLEMAS DE CORS
class IndexNowService {
  constructor() {
    this.apiKey = '26798a3d0e164f0b84aae9ecee70ad82';
    this.host = 'avaqualis.com';
    this.keyLocation = `https://${this.host}/${this.apiKey}.txt`;
  }

  // Método usando imagem invisível (contorna CORS)
  async submitSingleUrl(url) {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    return new Promise(resolve => {
      try {
        const img = new Image();

        const timeout = setTimeout(() => {
          console.log(`✅ IndexNow: URL processada (timeout esperado): ${url}`);
          resolve({ success: true, url, method: 'image-timeout' });
        }, 5000);

        img.onload = () => {
          clearTimeout(timeout);
          console.log(`✅ IndexNow: URL submetida com sucesso: ${url}`);
          resolve({ success: true, url, method: 'image-load' });
        };

        img.onerror = () => {
          clearTimeout(timeout);
          // Erro é esperado pois a API retorna dados, não imagem
          console.log(`✅ IndexNow: URL submetida (resposta da API recebida): ${url}`);
          resolve({ success: true, url, method: 'image-api-response' });
        };

        // Usar Bing que tem menos restrições
        const bingUrl = `https://www.bing.com/indexnow?url=${encodeURIComponent(url)}&key=${this.apiKey}`;
        img.src = bingUrl;
      } catch (error) {
        console.warn(`⚠️ IndexNow: Erro ao submeter: ${url}`, error);
        resolve({ success: false, url, error: error.message });
      }
    });
  }

  // Submeter múltiplas URLs
  async submitMultipleUrls(urls) {
    if (Platform.OS !== 'web') return;

    console.log(`🚀 IndexNow: Processando ${urls.length} URLs...`);

    const results = [];
    for (const url of urls) {
      const result = await this.submitSingleUrl(url);
      results.push(result);
      // Delay entre requisições para evitar spam
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    const successCount = results.filter(r => r.success).length;
    console.log(`✅ IndexNow: ${successCount}/${urls.length} URLs processadas`);

    return { success: successCount > 0, count: successCount, total: urls.length };
  }

  // Gerar URLs principais do site
  generateSiteUrls() {
    const baseUrl = `https://${this.host}`;
    return [
      baseUrl, // Página inicial
      `${baseUrl}/formularios`, // Lista de formulários
      `${baseUrl}/criar-formulario`, // Criador de formulários
    ];
  }

  // Submeter todas as URLs principais do site
  async submitAllSiteUrls() {
    const urls = this.generateSiteUrls();
    return await this.submitMultipleUrls(urls);
  }
}

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentScreen, setCurrentScreen] = useState('list');
  const [selectedForm, setSelectedForm] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [indexNow] = useState(() => new IndexNowService());
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [streamId, setStreamId] = useState(null);

  // ✅ NOVOS ESTADOS PARA SINCRONIZAÇÃO E AUTENTICAÇÃO
  const [serverStatus, setServerStatus] = useState('checking');
  const [syncStatus, setSyncStatus] = useState({ isSyncing: false, lastSyncTime: null });
  const [currentUser, setCurrentUser] = useState(null);

  // ✅ NOVO ESTADO PARA SIMULAÇÃO DE USUÁRIO
  const [simulatedUser, setSimulatedUser] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);

  // Função helper para obter o usuário efetivo (real ou simulado)
  const getEffectiveUser = () => {
    return isSimulating && simulatedUser ? simulatedUser : currentUser;
  };

  useEffect(() => {
  console.log('🔍 DEBUG ValidationRedirect: useEffect executado', {
    currentUser: currentUser?.nome,
    isLoading,
    Platform: Platform.OS
  });
  
  // Verificar se deve redirecionar para ValidationScreen após reload
  const checkValidationRedirect = async () => {
    try {
      console.log('🔍 DEBUG: checkValidationRedirect iniciado');
      
      let shouldRedirect = false;
      let message = '';

      if (Platform.OS === 'web') {
        shouldRedirect = localStorage.getItem('redirectToValidation') === 'true';
        message = localStorage.getItem('validationMessage') || '';
        
        console.log('🔍 DEBUG: localStorage values:', {
          redirectToValidation: localStorage.getItem('redirectToValidation'),
          validationMessage: message,
          shouldRedirect
        });
        
        // Limpar os indicadores
        if (shouldRedirect) {
          console.log('🔍 DEBUG: Limpando localStorage...');
          localStorage.removeItem('redirectToValidation');
          localStorage.removeItem('validationMessage');
        }
      } else {
        shouldRedirect = await AsyncStorage.getItem('redirectToValidation') === 'true';
        message = await AsyncStorage.getItem('validationMessage') || '';
        
        // Limpar os indicadores
        if (shouldRedirect) {
          await AsyncStorage.removeItem('redirectToValidation');
          await AsyncStorage.removeItem('validationMessage');
        }
      }

      if (shouldRedirect && currentUser) {
        console.log('🔍 DEBUG: shouldRedirect é true e tem currentUser:', {
          userLevel: currentUser.level,
          userName: currentUser.nome
        });
        
        // Verificar se o usuário tem permissão
        if (currentUser.level === 'programador' || currentUser.level === 'coordenacao') {
          console.log('🔄 Redirecionando para ValidationScreen após validação');
          setCurrentScreen('validation');
          
          // Mostrar mensagem de sucesso opcional
          if (message === 'validated') {
            setTimeout(() => {
              Alert.alert('✅ Sucesso', 'Formulário validado com sucesso!');
            }, 1000);
          } else if (message === 'revalidated') {
            setTimeout(() => {
              Alert.alert('✅ Sucesso', 'Formulário revalidado com sucesso!');
            }, 1000);
          }
        } else {
          console.log('🔍 DEBUG: Usuário sem permissão para ValidationScreen');
        }
      } else {
        console.log('🔍 DEBUG: Não vai redirecionar:', {
          shouldRedirect,
          hasCurrentUser: !!currentUser
        });
      }
    } catch (error) {
      console.error('Erro ao verificar redirecionamento:', error);
    }
  };

  if (currentUser && !isLoading) {
    console.log('🔍 DEBUG: Chamando checkValidationRedirect...');
    checkValidationRedirect();
  } else {
    console.log('🔍 DEBUG: Não chamou checkValidationRedirect:', {
      hasCurrentUser: !!currentUser,
      isLoading
    });
  }
}, [currentUser, isLoading]);

  // Função helper para verificar permissões - CORRIGIDA
  const hasPermission = action => {
    const user = getEffectiveUser();
    if (!user) return false;

    // Debug log detalhado
    console.log(`🔍 Verificando permissão: ${action} para usuário:`, {
      id: user.id,
      nome: user.nome,
      level: user.level,
      email: user.email,
      isSimulating: isSimulating,
    });

    switch (action) {
      case 'view_results':
        return user.level === 'coordenacao' || user.level === 'programador';
      case 'create_form':
        // Apenas coordenação e programador podem criar formulários
        const canCreate = user.level === 'coordenacao' || user.level === 'programador';
        console.log(`✅ Pode criar formulário: ${canCreate} (level: ${user.level})`);
        return canCreate;

      case 'view_simulation':
        // Apenas o usuário real (não simulado) com nível coordenacao ou programador pode simular
        if (!currentUser) {
          console.log(`✅ Pode simular: false (sem usuário logado)`);
          return false;
        }
        const canSimulate =
          !isSimulating &&
          (currentUser.level === 'coordenacao' || currentUser.level === 'programador');
        console.log(
          `✅ Pode simular: ${canSimulate} (currentUser.level: ${currentUser?.level}, isSimulating: ${isSimulating})`
        );
        return canSimulate;

      default:
        return true;
    }
  };

  useEffect(() => {
    // Inicializar autenticação
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const result = await AuthService.initialize();
        const avaliador = AuthService.getAvaliador();
        setCurrentUser(avaliador);
        console.log('✅ Usuário inicializado:', {
          id: avaliador?.id,
          nome: avaliador?.nome,
          level: avaliador?.level,
          email: avaliador?.email,
        });
      } catch (error) {
        console.error('Erro ao inicializar autenticação:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // ✅ MODIFICAÇÃO 1: useEffect PARA DETECTAR STREAM NA URL - ATUALIZADO
  useEffect(() => {
    // Detectar se está acessando um stream via URL
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const currentUrl = window.location.pathname; // Usar pathname ao invés de href
      const streamMatch = currentUrl.match(/\/stream\/(\d{3,4})/); // Aceita 3 ou 4 dígitos

      if (streamMatch && streamMatch[1]) {
        console.log('🎥 Stream detectado na URL:', streamMatch[1]);
        setStreamId(streamMatch[1]);
        setCurrentScreen('stream');

        // Se ainda estiver carregando, aguardar um pouco
        if (isLoading) {
          setIsLoading(false);
        }
      }
    }
  }, []); // Executar apenas uma vez na montagem

  // ✅ MODIFICAÇÃO 2: NOVO useEffect PARA MONITORAR MUDANÇAS DE URL
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // Função para verificar URL
      const checkUrlForStream = () => {
        const currentUrl = window.location.pathname;
        const streamMatch = currentUrl.match(/\/stream\/(\d{3,4})/); // Aceita 3 ou 4 dígitos

        if (streamMatch && streamMatch[1]) {
          if (streamId !== streamMatch[1]) {
            console.log('🎥 Novo stream detectado:', streamMatch[1]);
            setStreamId(streamMatch[1]);
            setCurrentScreen('stream');
          }
        } else if (currentScreen === 'stream' && !streamMatch) {
          // Se estava no stream mas saiu
          setCurrentScreen('list');
          setStreamId(null);
        }
      };

      // Verificar inicialmente
      checkUrlForStream();

      // Adicionar listener para mudanças de URL
      window.addEventListener('popstate', checkUrlForStream);

      return () => {
        window.removeEventListener('popstate', checkUrlForStream);
      };
    }
  }, [currentScreen, streamId]);

  useEffect(() => {
    if (!currentUser) return;

    // Verificar status do banner
    const checkAnnouncementStatus = async () => {
      try {
        const dismissed = await AsyncStorage.getItem('announcement_dismissed');
        if (dismissed === 'true') {
          setShowAnnouncement(false);
        } else {
          setShowAnnouncement(false); // Mantendo false conforme o código original
        }
      } catch (error) {
        console.log('Erro ao verificar status do banner:', error);
        setShowAnnouncement(false);
      }
    };
    checkAnnouncementStatus();

    // ✅ CONFIGURAÇÃO INICIAL DO INDEXNOW
    const setupIndexNow = async () => {
      if (Platform.OS === 'web') {
        try {
          console.log('🚀 Inicializando IndexNow...');
          await indexNow.submitAllSiteUrls();
          console.log('✅ IndexNow configurado com sucesso');

          // Adicionar função de teste global para debug
          if (typeof window !== 'undefined') {
            window.testIndexNow = async () => {
              console.log('🧪 Testando IndexNow manualmente...');
              await indexNow.submitSingleUrl('https://avaqualis.com/');
              console.log('✅ Teste IndexNow concluído!');
            };
          }
        } catch (error) {
          console.warn('⚠️ Erro na configuração IndexNow:', error);
        }
      }
    };

    setupIndexNow();

    // ✅ NOVA FUNÇÃO - VERIFICAR CONEXÃO COM SERVIDOR
    const checkConnection = async () => {
      try {
        const isConnected = await SyncService.checkServerConnection();
        setServerStatus(isConnected ? 'online' : 'offline');

        // Iniciar sincronização automática apenas se não for visitante
        if (!AuthService.isVisitorMode()) {
          SyncService.startAutoSync(15); // A cada 15 minutos
        }

        // Configurar atualizações de status
        const intervalId = setInterval(() => {
          const status = SyncService.getStatus();
          setSyncStatus(status);

          // Verificar conexão periodicamente
          SyncService.checkServerConnection().then(isConnected =>
            setServerStatus(isConnected ? 'online' : 'offline')
          );
        }, 30000); // A cada 30 segundos

        // Limpar intervalo quando o componente for desmontado
        return () => {
          clearInterval(intervalId);
          SyncService.stopAutoSync();
        };
      } catch (error) {
        console.error('Erro ao verificar conexão:', error);
        setServerStatus('error');
      }
    };

    checkConnection();
  }, [indexNow, currentUser]);

  const handleCloseAnnouncement = async () => {
    try {
      await AsyncStorage.setItem('announcement_dismissed', 'true');
      setShowAnnouncement(false);
    } catch (error) {
      console.log('Erro ao salvar status do banner:', error);
      setShowAnnouncement(false);
    }
  };

  // ✅ NOVA FUNÇÃO - SINCRONIZAÇÃO MANUAL
  const handleManualSync = () => {
    if (AuthService.isVisitorMode()) {
      alert('Visitantes não podem sincronizar dados com o servidor.');
      return;
    }

    SyncService.syncNow()
      .then(() => {
        const status = SyncService.getStatus();
        setSyncStatus(status);
      })
      .catch(error => console.error('Erro na sincronização manual:', error));
  };

  // ✅ FUNÇÃO CORRIGIDA PARA LOGOUT
  const handleLogout = async () => {
    try {
      console.log('🚪 App.js - Iniciando processo de logout...');

      // Limpar simulação se estiver ativa
      if (isSimulating) {
        setIsSimulating(false);
        setSimulatedUser(null);
      }

      // Parar sincronização se estiver ativa
      if (!AuthService.isVisitorMode()) {
        SyncService.stopAutoSync();
      }

      // Fazer logout
      const result = await AuthService.logout();
      console.log('🚪 App.js - Resultado do logout:', result);

      if (result.success) {
        console.log('✅ App.js - Logout realizado com sucesso');

        // Limpar todos os estados
        setCurrentUser(null);
        setCurrentScreen('list');
        setSelectedForm(null);
        setShowLoginModal(false);
        setStreamId(null);

        // Aguardar um pouco e recarregar
        setTimeout(() => {
          console.log('🔄 Recarregando aplicação...');
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.location.reload();
          } else {
            // Para mobile, reinicializar
            setIsLoading(true);
            AuthService.initialize().then(result => {
              setCurrentUser(AuthService.getAvaliador());
              setIsLoading(false);
            });
          }
        }, 100);
      } else {
        console.error('❌ App.js - Falha no logout:', result.error);
        if (Platform.OS === 'web') {
          alert('Não foi possível fazer logout. Tente novamente.');
        }
      }
    } catch (error) {
      console.error('❌ App.js - Erro ao fazer logout:', error);
      if (Platform.OS === 'web') {
        alert('Ocorreu um erro ao fazer logout.');
      }
    }
  };

  // Função para abrir modal de login
  const handleOpenLogin = () => {
    setShowLoginModal(true);
  };

  // Função chamada após login bem-sucedido - CORRIGIDA
  const handleLoginSuccess = () => {
    const avaliador = AuthService.getAvaliador();
    console.log('🎉 App.js - Login bem-sucedido! Avaliador recebido:', {
      id: avaliador?.id,
      nome: avaliador?.nome,
      level: avaliador?.level,
      email: avaliador?.email,
      levelType: typeof avaliador?.level,
    });

    setCurrentUser(avaliador);
    setShowLoginModal(false);
    console.log('✅ Login bem-sucedido, usuário atual:', {
      id: avaliador?.id,
      nome: avaliador?.nome,
      level: avaliador?.level,
      email: avaliador?.email,
    });
  };

  const handleSelectForm = async form => {
    console.log('📝 App.js: Navegando para FormDetailScreen com formulário:', form);
    setSelectedForm(form);
    setCurrentScreen('form');

    // NOTIFICAR INDEXNOW SOBRE MUDANÇA DE PÁGINA
    if (form && form.id) {
      await indexNow.submitSingleUrl(`https://avaqualis.com/formulario/${form.id}`);
    }
  };

  // CORREÇÃO CRÍTICA: Função handleViewResponses corrigida
  const handleViewResponses = async () => {
    console.log('📁 App.js: Navegando para ResponsesScreen');
    setCurrentScreen('responses');

    // NOTIFICAR INDEXNOW SOBRE MUDANÇA DE PÁGINA
    await indexNow.submitSingleUrl('https://avaqualis.com/respostas');
  };

  // Adicione a função para navegar para a tela de resultados:
  const handleViewResults = () => {
    setCurrentScreen('results');
  };

  // Adicione/modifique a função para editar formulário da tela de resultados:
  const handleEditFormFromResults = form => {
    // IMPORTANTE: A flag isEditingFromResults já vem no objeto form
    setSelectedForm(form);
    setCurrentScreen('form');
  };

  const handleCreateForm = async () => {
    // Verificar permissão considerando simulação
    if (!hasPermission('create_form')) {
      const effectiveUser = getEffectiveUser();
      if (effectiveUser && effectiveUser.id === 'visitor') {
        alert(
          'Visitantes não podem criar formulários. Faça login para acessar esta funcionalidade.'
        );
      } else {
        alert(
          'Você não tem permissão para criar formulários. Esta funcionalidade está disponível apenas para Coordenação e Programadores.'
        );
      }
      return;
    }

    setCurrentScreen('creator');

    // NOTIFICAR INDEXNOW SOBRE MUDANÇA DE PÁGINA
    await indexNow.submitSingleUrl('https://avaqualis.com/criar-formulario');
  };

  // CORREÇÃO CRÍTICA: Função handleBackToList corrigida
  const handleBackToList = async () => {
    console.log('🔙 App.js: Navegando de volta para FormListScreen');
    setCurrentScreen('list');
    setSelectedForm(null);

    // NOTIFICAR INDEXNOW SOBRE VOLTA À LISTA
    await indexNow.submitSingleUrl('https://avaqualis.com/formularios');
  };

  const handleBackFromCreator = async () => {
    setCurrentScreen('list');

    // NOTIFICAR INDEXNOW SOBRE VOLTA À LISTA
    await indexNow.submitSingleUrl('https://avaqualis.com/formularios');
  };

  // CORREÇÃO CRÍTICA: Função handleSelectFormFromResponses corrigida
  const handleSelectFormFromResponses = async form => {
    console.log(
      '📝 App.js: Navegando de ResponsesScreen para FormDetailScreen com formulário:',
      form
    );
    setSelectedForm(form);
    setCurrentScreen('form');

    // NOTIFICAR INDEXNOW SOBRE MUDANÇA DE PÁGINA
    if (form && form.id) {
      await indexNow.submitSingleUrl(`https://avaqualis.com/formulario/${form.id}`);
    }
  };

  // ✅ NOVA FUNÇÃO - QUANDO FORMULÁRIO É CRIADO COM SUCESSO
  const handleFormCreationSuccess = async form => {
    setSelectedForm(form);
    setCurrentScreen('form');

    // NOTIFICAR INDEXNOW SOBRE NOVO FORMULÁRIO
    if (form && form.id) {
      await indexNow.submitSingleUrl(`https://avaqualis.com/formulario/${form.id}`);
    }
  };

  // ✅ NOVAS FUNÇÕES PARA SIMULAÇÃO - CORRIGIDAS
  const handleStartSimulation = user => {
    console.log('🎭 App.js - Iniciando simulação como:', {
      id: user.id,
      nome: user.nome,
      level: user.level,
    });
    setSimulatedUser(user);
    setIsSimulating(true);
  };

  const handleStopSimulation = () => {
    console.log('🎭 App.js - Parando simulação');
    setSimulatedUser(null);
    setIsSimulating(false);
  };

  // ✅ NOVA FUNÇÃO PARA NAVEGAR PARA VALIDAÇÃO
  const handleViewValidation = () => {
    const effectiveUser = getEffectiveUser();
    if (effectiveUser?.level === 'programador' || effectiveUser?.level === 'coordenacao') {
      setCurrentScreen('validation');
    }
  };

  // ✅ FUNÇÃO PARA EDITAR RESPOSTA DA VALIDAÇÃO
  const handleEditResponseFromValidation = response => {
    // Salvar a resposta atual para edição
    setSelectedForm({
      id: response.formId,
      title: response.formTitle,
      isEditingFromResults: true,
      currentProgress: {
        answered: Object.keys(response.answers || {}).length,
        total: response.totalQuestions || 0,
      },
      existingResponse: response,
    });
    setCurrentScreen('form');
  };

  // ✅ MODIFICAÇÃO 4: renderScreen ATUALIZADO COM VERIFICAÇÃO DE LOADING E MELHORIAS
  const renderScreen = () => {
    // Se está carregando e tem streamId na URL, mostrar loading especial
    if (isLoading && streamId && currentScreen === 'stream') {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#A4C639" />
          <Text style={styles.loadingText}>🎥 Carregando stream...</Text>
        </View>
      );
    }

    const effectiveUser = getEffectiveUser();

    switch (currentScreen) {
      // ✅ MODIFICAÇÃO 3: CASO 'stream' ATUALIZADO
      case 'stream':
        // Se não tem streamId, voltar para lista
        if (!streamId) {
          setCurrentScreen('list');
          return null;
        }

        // Se ainda está carregando a autenticação, mostrar loading
        if (!currentUser) {
          return (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#A4C639" />
              <Text style={styles.loadingText}>🎥 Carregando stream...</Text>
            </View>
          );
        }

        return (
          <StreamViewScreen
            route={{ params: { streamId: streamId } }}
            navigation={{
              goBack: () => {
                console.log('🔙 Voltando do stream');
                setCurrentScreen('list');
                setStreamId(null);
                // Limpar URL se estiver na web
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  window.history.pushState({}, '', '/');
                }
              },
            }}
          />
        );

      case 'results':
        return (
          <ResultsScreen
            onBack={() => setCurrentScreen('list')}
            currentUser={getEffectiveUser()}
            onEditForm={handleEditFormFromResults}
          />
        );

      case 'validation':
        return (
          <ValidationScreen
            onBack={() => setCurrentScreen('list')} // CORREÇÃO: 'list' ao invés de 'form_list'
            onEditResponse={form => {
              console.log(
                '🎯 App.js: Navegando para edição de formulário em modo validação:',
                form.id
              );
              setSelectedForm(form);
              setCurrentScreen('form'); // CORREÇÃO: 'form' ao invés de 'form_detail'
            }}
          />
        );

      case 'form':
        return (
          <FormDetailScreen
            form={selectedForm}
            onBack={handleBackToList} // CORREÇÃO: Sempre voltar para FormListScreen
            onViewResponses={handleViewResponses} // CORREÇÃO: Navegar para ResponsesScreen
            currentUser={effectiveUser}
            isVisitor={effectiveUser?.id === 'visitor'}
            onFormUpdated={data => {
              // Callback opcional para atualizações do formulário
              console.log('📝 Formulário atualizado:', data);
            }}
          />
        );

      case 'responses':
        return (
          <ResponsesScreen
            onBack={handleBackToList} // CORREÇÃO CRÍTICA: Voltar sempre para FormListScreen
            onSelectForm={handleSelectFormFromResponses} // CORREÇÃO: Navegar para FormDetailScreen
            currentUser={effectiveUser}
            isVisitor={effectiveUser?.id === 'visitor'}
          />
        );

      case 'creator':
        return (
          <FormCreatorScreen
            onBack={handleBackFromCreator}
            onSaveSuccess={handleFormCreationSuccess}
            currentUser={effectiveUser}
          />
        );

      default:
        return (
          <FormListScreen
            onSelectForm={handleSelectForm}
            onViewResponses={handleViewResponses}
            onCreateForm={handleCreateForm}
            currentUser={effectiveUser}
            isVisitor={effectiveUser?.id === 'visitor'}
            canCreateForm={hasPermission('create_form')}
            isSimulating={isSimulating}
            simulatedUser={simulatedUser}
            onOpenLogin={handleOpenLogin}
          />
        );
    }
  };

  // CONTEÚDO SEO ESTRUTURADO (INVISÍVEL MAS INDEXÁVEL)
  const seoContent = (
    <View style={styles.seoContent}>
      <Text style={styles.seoH1}>
        AVAQualis.com - Protótipo de Ferramenta Mobile para Programa AVAQualis/EBSERH
      </Text>
      <Text style={styles.seoH2}>
        Protótipo em desenvolvimento para auxiliar na avaliação do programa AVAQualis no HC-UFU
      </Text>
      <Text style={styles.seoDescription}>
        AVAQualis.com é um protótipo de ferramenta mobile desenvolvido pelas unidades UGITS + UGQ do
        HC-UFU/EBSERH para auxiliar na avaliação do programa AVAQualis. O programa AVAQualis é o
        programa oficial de acreditação hospitalar da rede EBSERH.
      </Text>
    </View>
  );

  // Tela de loading
  if (isLoading) {
    return (
      <PaperProvider theme={paperTheme}>
        <View style={[styles.container, styles.loadingContainer]}>
          <ActivityIndicator size="large" color="#A4C639" />
          <Text style={styles.loadingText}>Carregando...</Text>
        </View>
      </PaperProvider>
    );
  }

  // Aplicação principal - sempre mostra as telas (usuário já está logado como visitante)
  return (
    <PaperProvider theme={paperTheme}>
      {/* CONTEÚDO SEO INVISÍVEL MAS INDEXÁVEL */}
      {seoContent}

      <StatusBar barStyle="dark-content" backgroundColor="#A4C639" />
      <SafeAreaView style={styles.container}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          {/* BANNER DE ANÚNCIO CUSTOMIZADO */}
          {showAnnouncement && <AnnouncementBanner onClose={handleCloseAnnouncement} />}

          <HeaderComponent
            currentScreen={currentScreen}
            onBackPress={() => {
              console.log('🔙 HeaderComponent: Botão voltar pressionado na tela:', currentScreen);
              if (currentScreen === 'form') {
                handleBackToList();
              } else if (currentScreen === 'creator') {
                handleBackFromCreator();
              } else if (currentScreen === 'responses') {
                handleBackToList(); // CORREÇÃO CRÍTICA: Voltar sempre para FormListScreen
              } else if (currentScreen === 'results') {
                setCurrentScreen('list');
              } else if (currentScreen === 'validation') {
                setCurrentScreen('list');
              } else if (currentScreen === 'stream') {
                setCurrentScreen('list');
                setStreamId(null);
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  window.history.pushState({}, '', '/');
                }
              }
            }}
            serverStatus={serverStatus}
            syncStatus={syncStatus}
            onSyncPress={handleManualSync}
            currentUser={currentUser}
            onLogout={handleLogout}
            onOpenLogin={handleOpenLogin}
            canCreateForm={hasPermission('create_form')}
            canSimulate={hasPermission('view_simulation')}
            isSimulating={isSimulating}
            simulatedUser={simulatedUser}
            onStartSimulation={handleStartSimulation}
            onStopSimulation={handleStopSimulation}
            onCreateForm={handleCreateForm}
            onViewResults={handleViewResults} // Nova prop
            canViewResults={hasPermission('view_results')} // Nova prop
            canValidate={hasPermission('view_results')} // Mesma permissão que Results
            onValidate={handleViewValidation} // Nova prop
          />

          <View style={styles.content}>{renderScreen()}</View>

          <FooterComponent />
        </ScrollView>
      </SafeAreaView>

      {/* Modal de Login */}
      <LoginModal
        visible={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </PaperProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#A4C639',
  },
  scrollContent: {
    flexGrow: 1,
    backgroundColor: '#FFFFFF',
    justifyContent: 'flex-start',
  },
  content: {
    flex: 1,
  },
  // ESTILOS PARA CONTEÚDO SEO (INVISÍVEL MAS INDEXÁVEL)
  seoContent: {
    position: 'absolute',
    left: -9999,
    top: -9999,
    width: 1,
    height: 1,
    overflow: 'hidden',
  },
  seoH1: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#A4C639',
  },
  seoH2: {
    fontSize: 20,
    fontWeight: '600',
    color: '#7A9B00',
  },
  seoDescription: {
    fontSize: 16,
    lineHeight: 24,
    color: '#666666',
    marginVertical: 8,
  },
});
